// FamilyMembersList.js
import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  Autocomplete,
  IconButton,
  Box,
  Avatar,
  Typography,
  FormControl,
  FormHelperText,
  FormLabel,
  Skeleton,
} from "@mui/material";
import Icon from "@mui/material/Icon";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ShareIcon from '@mui/icons-material/Share';
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import { Country, State } from "country-state-city";
import { useSelector, useDispatch } from "react-redux";
import Male from "../../assets/images/male.png";
import Female from "../../assets/images/female.png";
import { setUserPhoto } from "../../Redux/action/sessionAction";
import Webcam from "react-webcam";
import PhoneInput, { isValidPhoneNumber, parsePhoneNumber, getCountryCallingCode } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import PhoneCountryAutocomplete from "./components/PhoneCountryAutocomplete";
import PincodeAutocomplete from "./components/PincodeAutocomplete";
import FamilyMemberDialog from "./FamilyMemberDialog";
import ViewFamilyMemberDialog from "./components/ViewFamilyMemberDialog";
import ShareProfileDialog from "components/ShareProfileDialog";
import MDSnackbar from "components/MDSnackbar";
import MDButton from "components/MDButton";
import ExistingMemberLinkDialog from "./components/ExistingMemberLinkDialog";
import { fetchMasterData } from "../../services/masterDataService";
import { fetchEducationDetailsByParent } from "../../services/educationService";
import { fetchWorkDetailsByParent } from "../../services/workService";
import { fetchIDCardDetailsByParent } from "../../services/idCardService";
import { fetchBankDetailsByParent } from "../../services/bankDetailsService";
import { fetchSocialLinksByParent } from "../../services/socialLinksService";
import { CITIES } from "../../data/cities";

const FamilyMembersList = ({ familyMembers, loading = false, onDelete, onUpdate, refreshFamilyData, refreshUserProfile, onOpenAddFamily, familyGroupId = "" }) => {
  const navigate = useNavigate();
  const sessionId = useSelector((state) => state.session.sessionId);
  const user_id = useSelector((state) =>
    state.session.setUserId ? state.session.setUserId.value : null
  );
  const user = useSelector((state) => state.session.setUser);
  const userPhoto = useSelector((state) =>
    state.session.setUserPhoto ? state.session.setUserPhoto : null
  );
  const dispatch = useDispatch();
  const userId = JSON.parse(localStorage.getItem('userId') || '{}').value;
  const currentUserId = user_id ?? userId;
  const isMyselfMember = useCallback(
    (m) => {
      if (!m || currentUserId == null) return false;
      const linkedId = m.linkedUserId ?? m.linked_user_id_c ?? m.linked_user_id ?? m.linked_userid;
      if (linkedId != null) return String(linkedId) === String(currentUserId);
      const ownerId = m.userId ?? m.users_id_c ?? m.owner_id;
      if (ownerId != null) return String(ownerId) === String(currentUserId);
      return String(m.id) === String(currentUserId);
    },
    [currentUserId]
  );
  const [castes, setCastes] = useState([]);
  const [religions, setReligions] = useState([]);
  const [relationships, setRelationships] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (!sessionId) return;
    const loadMasterData = async () => {
      try {
        const data = await fetchMasterData(sessionId);
        setCastes(data.castes);
        setReligions(data.religions);
        setRelationships(data.relationships);
      } catch (error) {
        console.error("Failed to load master data:", error);
        setCastes([]);
        setReligions([]);
        setRelationships([]);
      }
    };
    loadMasterData();
  }, [sessionId]);

  // Build display list directly from API response, sorted with myself first
  const displayFamilyMembers = useMemo(() => {
    const members = Array.isArray(familyMembers) ? familyMembers : [];
    return members.sort((a, b) => {
      const aIsMyself = isMyselfMember(a);
      const bIsMyself = isMyselfMember(b);
      if (aIsMyself && !bIsMyself) return -1;
      if (!aIsMyself && bIsMyself) return 1;
      return 0;
    });
  }, [familyMembers, isMyselfMember]);
  
  // Filter family members based on search term
  const filteredFamilyMembers = useMemo(() => {
    if (!searchTerm.trim()) return displayFamilyMembers;
    const term = searchTerm.toLowerCase();
    return displayFamilyMembers.filter((member) => {
      const name = (member.name || '').toLowerCase();
      const mobile = (member.mobile || '').toLowerCase();
      const email = (member.email || '').toLowerCase();
      return name.includes(term) || mobile.includes(term) || email.includes(term);
    });
  }, [displayFamilyMembers, searchTerm]);
  
  // Reduce console noise: log only when data changes and only in development
  
  // Build a quick index to resolve names from ids (spouse/parent lookups)
  const idToMember = useMemo(() => {
    const map = new Map();
    displayFamilyMembers.forEach((m) => {
      if (m && m.id != null) map.set(String(m.id), m);
    });
    return map;
  }, [displayFamilyMembers]);

  const collectParentIdentifiers = useCallback((entry) => {
    if (!entry) return [];
    return [
      entry.id,
      entry.record_id,
      entry.userId,
      entry.users_id_c,
      entry.user_id,
      entry.owner_id,
      entry.linkedUserId,
      entry.linked_user_id_c,
      entry.linked_user_id,
      entry.linked_userid,
      entry.spouseid_c,
      entry.spouseId,
      entry.parentid_c,
      entry.parentId,
    ]
      .filter((value) => value !== null && value !== undefined && String(value).trim() !== "")
      .map((value) => String(value));
  }, []);

  const findSpouseMember = useCallback((member) => {
    if (!member) return null;
    const mid = member.id != null ? String(member.id) : null;
    const spouseId = (member.spouseid_c ?? member.spouseId)
      ? String(member.spouseid_c ?? member.spouseId)
      : null;

    // 1) Direct link via spouseid_c
    if (spouseId && idToMember.has(spouseId)) {
      return idToMember.get(spouseId) || null;
    }
    // 2) Inverse link: someone pointing to this member
    for (const m of idToMember.values()) {
      const inverseSpouseId = m?.spouseid_c ?? m?.spouseId;
      if (m && inverseSpouseId && String(inverseSpouseId) === mid) {
        return m || null;
      }
    }
    // 3) Heuristics for Myself or spouse records
    if (isMyselfMember(member)) {
      const spouseRec = displayFamilyMembers.find((m) => {
        const rel = String(m?.relationship || m?.relation || m?.relationshipType || '').toLowerCase();
        return rel === 'husband' || rel === 'wife';
      });
      return spouseRec || null;
    }
    const rel = String(member?.relationship || member?.relation || member?.relationshipType || '').toLowerCase();
    if (rel === 'husband' || rel === 'wife') {
      // Spouse is likely Myself
      const me = displayFamilyMembers.find((m) => isMyselfMember(m));
      return me || null;
    }
    return null;
  }, [displayFamilyMembers, idToMember, isMyselfMember]);

  const findSpouseName = useCallback((member) => {
    const spouseMember = findSpouseMember(member);
    return spouseMember?.name || null;
  }, [findSpouseMember]);

  const findParentText = useCallback((member) => {
    if (!member) return null;
    const parentId = (member.parentid_c ?? member.parentId) ? String(member.parentid_c ?? member.parentId) : null;
    // 1) Direct parent by id
    if (parentId && idToMember.has(parentId)) {
      const p = idToMember.get(parentId);
      const names = [];
      if (p?.name) names.push(p.name);
      const parentSpouse = findSpouseMember(p);
      if (parentSpouse?.name) names.push(parentSpouse.name);
      const uniqueNames = [...new Set(names.filter(Boolean))];
      if (uniqueNames.length >= 2) return { label: 'Parents', text: uniqueNames.join(' • ') };
      if (uniqueNames.length === 1) return { label: 'Parent', text: uniqueNames[0] };
      return null;
    }
    // 2) For Myself, try Father/Mother if present
    if (isMyselfMember(member)) {
      const father = displayFamilyMembers.find((m) => String(m?.relationship || m?.relationshipType || '').toLowerCase() === 'father');
      const mother = displayFamilyMembers.find((m) => String(m?.relationship || m?.relationshipType || '').toLowerCase() === 'mother');
      const names = [father?.name, mother?.name].filter(Boolean);
      if (names.length === 2) return { label: 'Parents', text: names.join(' • ') };
      if (names.length === 1) return { label: 'Parent', text: names[0] };
    }
    return null;
  }, [displayFamilyMembers, findSpouseMember, idToMember, isMyselfMember]);

  const findChildrenText = useCallback((member) => {
    if (!member) return null;
    const memberId = member.id != null ? String(member.id) : null;
    const spouseMember = findSpouseMember(member);
    const parentIds = new Set([
      memberId,
      spouseMember?.id != null ? String(spouseMember.id) : null,
    ].filter(Boolean));

    const childNames = displayFamilyMembers
      .filter((candidate) => {
        if (!candidate || candidate.id == null || String(candidate.id) === memberId) return false;
        const parentId = candidate.parentId ?? candidate.parentid_c;
        return parentId != null && parentIds.has(String(parentId));
      })
      .map((candidate) => candidate.name)
      .filter(Boolean);

    const uniqueChildren = [...new Set(childNames)];
    if (!uniqueChildren.length) return null;
    return uniqueChildren.join(' • ');
  }, [displayFamilyMembers, findSpouseMember]);
  
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewingMember, setViewingMember] = useState(null);
  const [editingMember, setEditingMember] = useState(null);
  const [successSB, setSuccessSB] = useState(false);
  const [errorSB, setErrorSB] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  // Share dialog/list state
  const [shareOpen, setShareOpen] = useState(false);
  const [shareMember, setShareMember] = useState(null);
  const [shareEducationEntries, setShareEducationEntries] = useState([]);
  const [shareWorkEntries, setShareWorkEntries] = useState([]);
  const [shareIDCardEntries, setShareIDCardEntries] = useState([]);
  const [shareBankEntries, setShareBankEntries] = useState([]);
  const [shareAddressEntries, setShareAddressEntries] = useState([]);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({
    name: "",
    mobile: "",
    email: "",
    relationship: "",
    aadhar: "",
    caste: "",
    religion: "",
    address: "",
    address1_c: "",
    address2_c: "",
    district_c: "",
    city: "",
    pincode: "",
    state: null,
    country: null,
    instagram: "",
    facebook: "",
    youtube: "",
    linkedin: "",
    threads: "",
    // Personal Details
    age_c: "",
    alivestatus_c: "",
    birthplace_c: "",
    bloodgroup_c: "",
    dateofdeath_c: "",
    deathplace_c: "",
    deathreason_c: "",
    disability_c: "",
    disabilitytype_c: "",
    dob_c: "",
    gender_c: "",
    // Language Details
    languagesknown_c: "",
    mothertongue_c: "",
    // Marital Details
    maritalstatus_c: "",
    marriagedate_c: "",
    marriageplace_c: "",
    // Economic Status
    economical_status_c: "",
    // Education Details
    education1_c: "",
    education2_c: "",
    education3_c: "",
    education4_c: "",
    education5_c: "",
    educationtype1_c: "",
    educationtype2_c: "",
    educationtype3_c: "",
    educationtype4_c: "",
    educationtype5_c: "",
    institute1_c: "",
    institute2_c: "",
    institute3_c: "",
    institute4_c: "",
    institute5_c: "",
    yearofpassing1_c: "",
    yearofpassing2_c: "",
    yearofpassing3_c: "",
    yearofpassing4_c: "",
    yearofpassing5_c: "",
    // Work Details
    company_c: "",
    employmenttype_c: "",
    occupation_c: "",
    worklocation_c: "",
    photo: null,
    photoFile: null,
    photoRemoved: false, // Track if user explicitly removed photo
    phoneCountry: "IN",
  });
  // Track which fields have been touched/blurred by the user
  const [touchedFields, setTouchedFields] = useState({
    mobile: false,
    aadhar: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [cameraDialogOpen, setCameraDialogOpen] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const webcamRef = useRef(null);
  const fileInputRef = useRef(null);
  const [facingMode, setFacingMode] = useState('user');
  const [cameraError, setCameraError] = useState(null);
  const [cameraReady, setCameraReady] = useState(false);
  const [webcamKey, setWebcamKey] = useState(-1);
  const [cameraRetryCount, setCameraRetryCount] = useState(0);
  const [cameraMessage, setCameraMessage] = useState(null);
  const [videoDevices, setVideoDevices] = useState([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState(null);
  const triedDevicesRef = useRef(new Set());
  const [loosenConstraints, setLoosenConstraints] = useState(false);
  const [forceDefaultDevice, setForceDefaultDevice] = useState(false);
  const [useBareConstraints, setUseBareConstraints] = useState(false);

  // State to track visible education sections in edit dialog (initially show only Education 1)
  const [visibleEditEducationSections, setVisibleEditEducationSections] = useState(1);

  // Helper function to check if an education section is COMPLETELY filled (all 4 fields)
  const isEditEducationSectionFilled = (sectionNumber) => {
    return (
      editFormData[`education${sectionNumber}_c`]?.trim() &&
      editFormData[`educationtype${sectionNumber}_c`]?.trim() &&
      editFormData[`institute${sectionNumber}_c`]?.trim() &&
      editFormData[`yearofpassing${sectionNumber}_c`]?.trim()
    );
  };

  const MAX_CAMERA_RETRIES = 4;

  const resetTriedDevices = useCallback(() => {
    triedDevicesRef.current = new Set();
  }, []);

  const fetchVideoDevices = useCallback(async () => {
    if (typeof navigator === "undefined" || !navigator.mediaDevices) {
      throw new Error("Camera API not supported in this browser.");
    }

    const enumerateVideoInputs = async () => {
      const devices = await navigator.mediaDevices.enumerateDevices();
      return devices.filter((device) => device.kind === "videoinput");
    };

    let devices = await enumerateVideoInputs();

    if (!devices.length) {
      throw new Error("No camera devices found.");
    }

    if (devices.every((device) => !device.label)) {
      let tempStream;
      try {
        tempStream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: false,
        });
        devices = await enumerateVideoInputs();
      } catch (permissionError) {
        console.error(
          "Camera request failed while enumerating devices:",
          permissionError
        );

        const errorName = permissionError?.name;

        if (errorName === "NotAllowedError" || errorName === "PermissionDeniedError") {
          throw new Error("Camera permission was denied. Please allow camera access and try again.");
        }

        if (errorName === "NotReadableError") {
          throw new Error(
            "Your camera appears to be in use by another application. Please close any other app that might be using the camera and try again."
          );
        }

        if (errorName === "OverconstrainedError") {
          throw new Error(
            "No camera could satisfy the requested settings. Try lowering the camera quality or switching devices."
          );
        }

        if (errorName === "AbortError") {
          throw new Error(
            "The camera took too long to start. Please try again or switch to a different device."
          );
        }

        if (permissionError?.message) {
          throw new Error(permissionError.message);
        }

        throw permissionError;
      } finally {
        if (tempStream) {
          tempStream.getTracks().forEach((track) => track.stop());
        }
      }

      devices = devices.filter((device) => device.kind === "videoinput");

      if (!devices.length) {
        throw new Error("No camera devices found.");
      }
    }

    return devices;
  }, []);

  const selectDeviceIdForMode = useCallback((devices, mode, { preferUntried = false } = {}) => {
    if (!devices?.length) {
      return null;
    }

    const labelPattern =
      mode === "user"
        ? /(front|user|face|webcam)/i
        : /(back|rear|environment|world)/i;

    const consideredDevices = preferUntried
      ? devices.filter((device) => !triedDevicesRef.current.has(device.deviceId))
      : devices;

    const labeledMatch = consideredDevices.find((device) => labelPattern.test(device.label));

    if (labeledMatch) {
      return labeledMatch.deviceId;
    }

    const untriedFallback = devices.find((device) => !triedDevicesRef.current.has(device.deviceId));
    if (untriedFallback) {
      return untriedFallback.deviceId;
    }

    return consideredDevices[0]?.deviceId || devices[0]?.deviceId || null;
  }, []);

  const setActiveDevice = useCallback((deviceId) => {
    if (!deviceId) {
      return;
    }
    triedDevicesRef.current.add(deviceId);
    setSelectedDeviceId(deviceId);
    console.log('Setting active camera device', {
      deviceId,
      triedDevices: Array.from(triedDevicesRef.current),
    });
  }, [setSelectedDeviceId]);

  const stopCameraStream = () => {
    const stream = webcamRef.current?.stream || webcamRef.current?.video?.srcObject;
    if (stream) {
      const tracks = stream.getTracks ? stream.getTracks() : [];
      tracks.forEach((track) => track.stop());
    }
    if (webcamRef.current) {
      if (webcamRef.current.video) {
        webcamRef.current.video.srcObject = null;
      }
      webcamRef.current.stream = null;
    }
  };

  // Prefetch and open share dialog for a member
  const handleShareClick = async (member) => {
    if (!member || !sessionId) return;
    
    // Determine if this is "Myself" and prepare the correct member data
    const linkedId = member.linkedUserId ?? member.linked_user_id_c ?? member.linked_user_id ?? member.linked_userid;
    const isMySelf = linkedId != null
      ? String(linkedId) === String(currentUserId)
      : (member.id === "0" || member.id === 0 || (member.relationship && String(member.relationship).toLowerCase() === "myself"));
    const parentId = isMySelf ? (user_id || null) : (member.record_id || member.id || null);
    const parentInfo = findParentText(member);
    const spouseName = findSpouseName(member);
    const childrenText = findChildrenText(member);
    
    // Create member object with correct user_id for ShareProfileDialog
    const memberToShare = {
      ...member,
      user_id: isMySelf ? user_id : (member.user_id || member.record_id || member.id),
      parent_display: parentInfo?.text || "",
      parents_display: parentInfo?.label === "Parents" ? (parentInfo?.text || "") : "",
      spouse_display: spouseName || "",
      children_display: childrenText || "",
    };
    
    setShareMember(memberToShare);
    
    // Set entries to null to trigger loading state in ShareProfileDialog
    setShareEducationEntries(null);
    setShareWorkEntries(null);
    setShareIDCardEntries(null);
    setShareBankEntries(null);
    setShareAddressEntries(null);
    
    // Open the dialog immediately
    setShareOpen(true);

    // Fetch data in the background
    try {
      const [eduEntries, workEntries, idEntries, bankEntries, socialEntries] = await Promise.all([
        fetchEducationDetailsByParent(sessionId, parentId).catch(() => []),
        fetchWorkDetailsByParent(sessionId, parentId).catch(() => []),
        fetchIDCardDetailsByParent(sessionId, parentId).catch(() => []),
        fetchBankDetailsByParent(sessionId, parentId).catch(() => []),
        fetchSocialLinksByParent(sessionId, parentId).catch(() => []),
      ]);

      setShareEducationEntries(eduEntries || []);
      setShareWorkEntries(workEntries || []);
      setShareIDCardEntries(idEntries || []);
      setShareBankEntries(bankEntries || []);
      // Create a simple address entry only when at least one address field exists
      const fallbackAddress = {
        id: member.id || 'addr',
        address: member.address_c || member.address || '',
        city: member.city_c || member.city || '',
        state: member.state_c || member.state || '',
        country: member.country_c || member.country || '',
        pincode: member.pincode_c || member.pincode || '',
        addresstype_c: 'Member Address',
      };
      const hasFallbackAddress = [
        fallbackAddress.address,
        fallbackAddress.city,
        fallbackAddress.state,
        fallbackAddress.country,
        fallbackAddress.pincode,
      ].some((value) => String(value || '').trim() !== '');
      setShareAddressEntries(hasFallbackAddress ? [fallbackAddress] : []);
    } catch (err) {
      console.error('Failed to fetch share entries', err);
      // Set empty arrays on error so loading stops
      setShareEducationEntries([]);
      setShareWorkEntries([]);
      setShareIDCardEntries([]);
      setShareBankEntries([]);
      const fallbackAddress = {
        id: member.id || 'addr',
        address: member.address_c || member.address || '',
        city: member.city_c || member.city || '',
        state: member.state_c || member.state || '',
        country: member.country_c || member.country || '',
        pincode: member.pincode_c || member.pincode || '',
        addresstype_c: 'Member Address',
      };
      const hasFallbackAddress = [
        fallbackAddress.address,
        fallbackAddress.city,
        fallbackAddress.state,
        fallbackAddress.country,
        fallbackAddress.pincode,
      ].some((value) => String(value || '').trim() !== '');
      setShareAddressEntries(hasFallbackAddress ? [fallbackAddress] : []);
    }
  };

  const countries = Country.getAllCountries();
  const states = State.getAllStates();

  const phoneLabels = useMemo(() => {
    const labels = {
      ZZ: "International",
    };

    Country.getAllCountries().forEach(({ isoCode, name, phonecode }) => {
      const code = isoCode ? isoCode.toUpperCase() : null;
      if (!code) return;

      let callingCode = null;

      try {
        callingCode = getCountryCallingCode(code);
      } catch (error) {
        if (phonecode) {
          callingCode = phonecode.split(",")[0];
        }
      }

      if (callingCode) {
        labels[code] = `${name} (+${callingCode})`;
      } else {
        labels[code] = name;
      }
    });

    return labels;
  }, []);

  const normalizePhoneForState = useCallback((rawValue) => {
    if (!rawValue) {
      return { value: "", country: "IN" };
    }

    const trimmed = rawValue.toString().trim();

    try {
      const parsed = trimmed.startsWith("+")
        ? parsePhoneNumber(trimmed)
        : null;

      if (parsed) {
        return {
          value: parsed.number,
          country: parsed.country || "IN",
        };
      }
    } catch (error) {
      // Fallback handling below
    }

    if (/^\d{10}$/.test(trimmed)) {
      return { value: `+91${trimmed}`, country: "IN" };
    }

    if (/^\d+$/.test(trimmed)) {
      return { value: `+${trimmed}`, country: "IN" };
    }

    return { value: trimmed, country: "IN" };
  }, []);

  const handleViewClick = (member) => {
    if (isMyselfMember(member)) {
      navigate('/profile');
      return;
    }
    setViewingMember(member);
    setViewDialogOpen(true);
  };

  const handleViewClose = () => {
    setViewDialogOpen(false);
    setViewingMember(null);
  };

  const handleEditClick = (member) => {
    if (isMyselfMember(member)) return;
    const normalizedPhone = normalizePhoneForState(member.mobile || "");

    // Prepare member data with normalized phone for the unified dialog
    // For "Myself" (id === "0"), use the actual user ID from session for updates
    // For other family members, use their record_id
    const isMySelf = member.id === "0";
    const memberDataForEdit = {
      ...member,
      id: isMySelf ? user_id : (member.id || member.record_id),
      displayId: member.id, // Keep the original display ID ("0" for Myself)
      relation: member.relationship || "",
      mobile: normalizedPhone.value,
      phoneCountry: normalizedPhone.country,
      aadhar: member.aadhaar_c || member.aadhar_c || member.aadhar || "",
      state: member.state ? { name: member.state, isoCode: "" } : null,
      country: member.country ? { name: member.country, isoCode: "" } : null,
      photo: isMySelf ? (userPhoto || member.photo || null) : (member.photo_url_c || null),
      // Explicitly include relationship fields for dialog
      relationship_type_c: member.relationship_type_c || "",
      parentid_c: member.parentid_c || "",
      spouseid_c: member.spouseid_c || "",
    };
    
    setEditingMember(memberDataForEdit);
    setEditDialogOpen(true);
    setError(null);
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    
    if (name === "aadhar") {
      // Only allow digits (no alphabets or special characters)
      if (/^\d*$/.test(value)) {
        // Set exact length restrictions: 12 for aadhar
        const maxLength = 12;
        if (value.length <= maxLength) {
          setEditFormData((prev) => ({ ...prev, [name]: value }));
        }
      }
    } else {
      setEditFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleEditMobileChange = (value) => {
    setEditFormData((prev) => ({
      ...prev,
      mobile: value || "",
    }));
  };

  const handleEditMobileCountryChange = (country) => {
    setEditFormData((prev) => ({
      ...prev,
      phoneCountry: country || prev.phoneCountry || "IN",
    }));
  };

  const handleCountryChange = (event, newValue) => {
    setEditFormData((prev) => ({ ...prev, country: newValue, state: null }));
  };

  const handleStateChange = (event, newValue) => {
    setEditFormData((prev) => ({ ...prev, state: newValue }));
  };

  const handleRelationshipChange = (event, newValue) => {
    setEditFormData((prev) => ({ ...prev, relationship: newValue }));
  };

  const handleRelationInputChange = (event, newInputValue) => {
    setEditFormData((prev) => ({ ...prev, relationship: newInputValue }));
  };

  const handleCasteChange = (event, newValue) => {
    setEditFormData((prev) => ({ ...prev, caste: newValue || "" }));
  };

  const handleCasteInputChange = (event, newInputValue) => {
    setEditFormData((prev) => ({ ...prev, caste: newInputValue || "" }));
  };

  const handleReligionChange = (event, newValue) => {
    setEditFormData((prev) => ({ ...prev, religion: newValue || "" }));
  };

  const handleReligionInputChange = (event, newInputValue) => {
    setEditFormData((prev) => ({ ...prev, religion: newInputValue || "" }));
  };

  const handleCityChange = (event, newValue) => {
    setEditFormData((prev) => ({ ...prev, city: newValue || "" }));
  };

  const handleCityInputChange = (event, newInputValue) => {
    setEditFormData((prev) => ({ ...prev, city: newInputValue || "" }));
  };

  // Handle pincode change and auto-populate city, state, country
  const handleEditPincodeLocationChange = (locationData) => {
    if (locationData) {
      try {
        setEditFormData((prev) => ({
          ...prev,
          city: locationData.city || prev.city,
          state: locationData.state ? 
            states.find(s => s.name.toLowerCase() === locationData.state.toLowerCase()) || prev.state 
            : prev.state,
          country: locationData.country ? 
            countries.find(c => c.name.toLowerCase() === locationData.country.toLowerCase()) || prev.country 
            : prev.country,
        }));
      } catch (error) {
        console.error('Error updating location data:', error);
      }
    }
  };

  const handleEditPincodeChange = (pincode) => {
    setEditFormData((prev) => ({
      ...prev,
      pincode: pincode || "",
    }));
  };
  
  // Handle field blur to mark it as touched
  const handleFieldBlur = (fieldName) => {
    setTouchedFields(prev => ({
      ...prev,
      [fieldName]: true
    }));
  };

  const handlePhotoChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please select a valid image file (JPG, PNG, GIF)');
        event.target.value = ''; // Clear the input
        return;
      }
      
        const isEditingSelf = editingMember?.id === "0";
        const maxSize = 15 * 1024 * 1024;

      // Validate file size (max varies)
        if (file.size > maxSize) {
          setError('Image size should be less than 15MB');
        event.target.value = ''; // Clear the input
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        setEditFormData((prev) => ({ 
          ...prev, 
          photo: e.target.result,
          photoFile: file,
          photoRemoved: false // Reset removal flag when new photo is selected
        }));
        setError(null);
      };
      reader.onerror = () => {
        setError('Error reading the image file');
        event.target.value = ''; // Clear the input
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemovePhoto = () => {
    setEditFormData((prev) => ({ 
      ...prev, 
      photo: null,
      photoFile: null,
      photoRemoved: true // Mark that user explicitly removed the photo
    }));
    
    // Clear the file input as well
    const fileInput = document.querySelector('input[type="file"]');
    if (fileInput) {
      fileInput.value = '';
    }
    
    // Clear any error related to photo
    if (error && error.includes('Image')) {
      setError(null);
    }
  };

  const handleOpenCamera = async () => {
    stopCameraStream();
    setCapturedImage(null);
    setCameraReady(false);
    setCameraError(null);
    setCameraMessage('Requesting camera access...');
    setCameraRetryCount(0);
    resetTriedDevices();
    setWebcamKey(-1);
    setLoosenConstraints(false);
    setForceDefaultDevice(false);
    setUseBareConstraints(false);
    setCameraDialogOpen(true);

    // Add delay to allow dialog to fully render and previous camera resources to release
    await new Promise(resolve => setTimeout(resolve, 500));

    try {
      const devices = await fetchVideoDevices();
      setVideoDevices(devices);
      const hasSingleDevice = devices.length === 1;
      if (hasSingleDevice) {
        setForceDefaultDevice(true);
      }
      const deviceId = selectDeviceIdForMode(devices, facingMode) || devices[0]?.deviceId || null;

      if (!deviceId) {
        throw new Error('No camera devices found.');
      }

      setActiveDevice(deviceId);
      setCameraMessage('Initializing camera...');
      console.log('Initializing camera with device', {
        deviceId,
        facingMode,
        loosenConstraints: false,
        forceDefaultDevice: hasSingleDevice,
      });
      
      // Add longer delay before starting webcam to prevent AbortError
      await new Promise(resolve => setTimeout(resolve, 500));
      setWebcamKey((prev) => prev + 1);
    } catch (initError) {
      console.error('Camera initialization error:', initError);
      setCameraMessage(null);
      setCameraError(initError.message || 'Unable to access camera. Please check permissions.');
    }
  };

  const handleCloseCamera = () => {
    setCameraDialogOpen(false);
    setCapturedImage(null);
    setCameraReady(false);
    setCameraError(null);
    setCameraMessage(null);
    setSelectedDeviceId(null);
    setCameraRetryCount(0);
    resetTriedDevices();
    setWebcamKey(-1);
    setLoosenConstraints(false);
    setForceDefaultDevice(false);
    setUseBareConstraints(false);
    stopCameraStream();
  };

  const capturePhoto = () => {
    const imageSrc = webcamRef.current.getScreenshot();
    setCapturedImage(imageSrc);
  };

  const handleConfirmPhoto = () => {
    if (capturedImage) {
      // Convert base64 to blob for form data
      fetch(capturedImage)
        .then(res => res.blob())
        .then(blob => {
          const file = new File([blob], "captured-photo.jpg", { type: "image/jpeg" });
          setEditFormData((prev) => ({ 
            ...prev, 
            photo: capturedImage,
            photoFile: file,
            photoRemoved: false
          }));
          setError(null);
        });
    }
    setCameraDialogOpen(false);
    setCapturedImage(null);
    setCameraMessage(null);
    stopCameraStream();
  };

  const handleRetakePhoto = () => {
    setCapturedImage(null);
  };

  const switchCamera = async () => {
    stopCameraStream();
    setCameraReady(false);
    setCameraError(null);
    setCameraRetryCount(0);
    setWebcamKey(-1);
    setLoosenConstraints(false);
    setForceDefaultDevice(false);
    setUseBareConstraints(false);

    // Add longer delay to allow current camera to fully release
    await new Promise(resolve => setTimeout(resolve, 600));

    try {
      const devices = videoDevices.length ? videoDevices : await fetchVideoDevices();
      if (!videoDevices.length) {
        setVideoDevices(devices);
      }

      if (devices.length <= 1) {
        setFacingMode('user');
        setForceDefaultDevice(true);
        setCameraMessage('Only one camera detected on this device.');
        setWebcamKey((prev) => prev + 1);
        return;
      }

      const nextMode = facingMode === 'user' ? 'environment' : 'user';
      setFacingMode(nextMode);
      setCameraMessage(`Switching to ${nextMode === 'user' ? 'front' : 'rear'} camera...`);

      resetTriedDevices();
      const nextDeviceId = selectDeviceIdForMode(devices, nextMode) || devices[0]?.deviceId || null;

      if (!nextDeviceId) {
        throw new Error('No camera devices found.');
      }

      setActiveDevice(nextDeviceId);
      
      // Add longer delay before initializing new camera
      await new Promise(resolve => setTimeout(resolve, 500));
      setWebcamKey((prev) => prev + 1);
    } catch (switchError) {
      console.error('Switch camera failed:', switchError);
      setCameraMessage(null);
      setCameraError(switchError.message || 'Unable to switch camera. Please try again.');
    }
  };

  const retryCamera = async () => {
    stopCameraStream();
    setCameraError(null);
    setCameraReady(false);
    setCameraMessage('Retrying camera...');
    setCameraRetryCount(0);
    resetTriedDevices();
    setWebcamKey(-1);
    setLoosenConstraints(false);
    setForceDefaultDevice(false);
    setUseBareConstraints(false);

    // Add longer delay to allow camera resources to fully release
    await new Promise(resolve => setTimeout(resolve, 700));

    try {
      const devices = videoDevices.length ? videoDevices : await fetchVideoDevices();
      if (!videoDevices.length) {
        setVideoDevices(devices);
      }

      const hasSingleDevice = devices.length === 1;
      setForceDefaultDevice(hasSingleDevice);

      const deviceId = selectDeviceIdForMode(devices, facingMode) || devices[0]?.deviceId || null;

      if (!deviceId) {
        throw new Error('No camera devices found.');
      }

      setActiveDevice(deviceId);
      
      // Add longer delay before initializing camera
      await new Promise(resolve => setTimeout(resolve, 500));
      setWebcamKey((prev) => prev + 1);
    } catch (retryError) {
      console.error('Retry camera failed:', retryError);
      setCameraMessage(null);
      setCameraError(retryError.message || 'Unable to access camera. Please check permissions.');
    }
  };

  useEffect(() => {
    return () => {
      stopCameraStream();
    };
  }, []);

  // Update visible education sections in edit dialog when education fields change
  useEffect(() => {
    let maxVisibleSection = 1;
    for (let i = 1; i <= 4; i++) {
      if (isEditEducationSectionFilled(i)) {
        maxVisibleSection = i + 1;
      }
    }
    // Cap at 5 maximum
    maxVisibleSection = Math.min(maxVisibleSection, 5);
    setVisibleEditEducationSections(maxVisibleSection);
  }, [
    editFormData.education1_c, editFormData.educationtype1_c, editFormData.institute1_c, editFormData.yearofpassing1_c,
    editFormData.education2_c, editFormData.educationtype2_c, editFormData.institute2_c, editFormData.yearofpassing2_c,
    editFormData.education3_c, editFormData.educationtype3_c, editFormData.institute3_c, editFormData.yearofpassing3_c,
    editFormData.education4_c, editFormData.educationtype4_c, editFormData.institute4_c, editFormData.yearofpassing4_c,
  ]);

  const handleCameraReady = useCallback(() => {
    console.log('Camera ready');
    setCameraReady(true);
    setCameraError(null);
    setCameraMessage(null);
    setCameraRetryCount(0);
    setLoosenConstraints(false);
    setForceDefaultDevice(false);
    setUseBareConstraints(false);
  }, []);

  const handleCameraError = useCallback(async (error) => {
    console.error('Camera error:', error);
    console.error('Camera error details', {
      name: error?.name,
      message: error?.message,
      code: error?.code,
      facingMode,
      selectedDeviceId,
      loosenConstraints,
      triedDevices: Array.from(triedDevicesRef.current),
      retryCount: cameraRetryCount,
      timestamp: new Date().toISOString(),
    });
    stopCameraStream();
    setCameraReady(false);
    setCameraError(null);
    setWebcamKey(-1);

    if (error?.name === 'NotAllowedError' || error?.name === 'PermissionDeniedError') {
      setCameraMessage(null);
      setCameraError('Camera permission was denied. Please allow camera access in your browser settings and try again.');
      return;
    }

    if (error?.name === 'NotFoundError') {
      setCameraMessage(null);
      setCameraError('No camera devices were found. Please connect a camera and try again.');
      return;
    }

    if (error?.name === 'NotReadableError') {
      setCameraMessage(null);
      setCameraError('Your camera appears to be in use by another application. Please close any app using the camera and try again.');
      return;
    }

    if (error?.name === 'OverconstrainedError') {
      setCameraMessage(null);
      setCameraError('The selected camera cannot satisfy the requested settings. Try switching cameras or close other apps that may be reserving it.');
      return;
    }

    let devices = videoDevices;

    if (!devices.length) {
      try {
        devices = await fetchVideoDevices();
        setVideoDevices(devices);
      } catch (deviceError) {
        setCameraMessage(null);
        setCameraError(deviceError.message || 'Unable to access camera. Please check permissions.');
        return;
      }
    }

    if (error?.name === 'AbortError') {
      // Add longer delay before trying alternate device to prevent rapid retries
      await new Promise(resolve => setTimeout(resolve, 600));

      const scheduleReinitialization = (delay = 900) => {
        setTimeout(() => {
          stopCameraStream();
          setCameraError(null);
          setCameraReady(false);
          setWebcamKey((prev) => prev + 1);
        }, delay);
      };
      
      const alternateDeviceId = selectDeviceIdForMode(devices, facingMode, { preferUntried: true });

      if (alternateDeviceId && !triedDevicesRef.current.has(alternateDeviceId)) {
        setCameraMessage('Trying another camera device...');
        setActiveDevice(alternateDeviceId);
        // Add longer delay before reinitializing
        await new Promise(resolve => setTimeout(resolve, 500));
        setWebcamKey((prev) => prev + 1);
        return;
      }

      if (facingMode === 'environment') {
        const frontDeviceId =
          selectDeviceIdForMode(devices, 'user', { preferUntried: true }) ||
          selectDeviceIdForMode(devices, 'user');

        if (frontDeviceId && !triedDevicesRef.current.has(frontDeviceId)) {
          setCameraMessage('Rear camera not available. Switching to front camera...');
          resetTriedDevices();
          setActiveDevice(frontDeviceId);
          setFacingMode('user');
          // Add longer delay before reinitializing
          await new Promise(resolve => setTimeout(resolve, 500));
          setWebcamKey((prev) => prev + 1);
          return;
        }
      }

      if (!loosenConstraints) {
        setCameraMessage('Camera timed out. Retrying with lower video quality...');
        setLoosenConstraints(true);
        setCameraRetryCount((prev) => Math.min(prev + 1, MAX_CAMERA_RETRIES));
        scheduleReinitialization();
        return;
      }

      if (!forceDefaultDevice) {
        setCameraMessage('Camera timed out. Switching to browser default camera...');
        setForceDefaultDevice(true);
        resetTriedDevices();
        setSelectedDeviceId(null);
        setFacingMode('user');
        setCameraRetryCount((prev) => Math.min(prev + 1, MAX_CAMERA_RETRIES));
        scheduleReinitialization();
        return;
      }

      if (!useBareConstraints) {
        setCameraMessage('Camera timed out. Retrying with minimal settings...');
        setUseBareConstraints(true);
        setCameraRetryCount((prev) => Math.min(prev + 1, MAX_CAMERA_RETRIES));
        scheduleReinitialization(1000);
        return;
      }

      if (cameraRetryCount + 1 <= MAX_CAMERA_RETRIES) {
        setCameraMessage('Camera still loading. Retrying...');
        setCameraRetryCount((prev) => Math.min(prev + 1, MAX_CAMERA_RETRIES));
        scheduleReinitialization();
        return;
      }

      setCameraMessage(null);
      setCameraError('Camera initialization timed out. Please try again or check your camera permissions.');
      return;
    }

    setCameraMessage(null);
    setCameraError('Unable to access camera. Please check permissions.');
  }, [videoDevices, fetchVideoDevices, selectDeviceIdForMode, facingMode, cameraRetryCount, setActiveDevice, resetTriedDevices, setFacingMode, setVideoDevices, loosenConstraints, selectedDeviceId, forceDefaultDevice, useBareConstraints]);

  const videoConstraints = useMemo(() => {
    if (useBareConstraints) {
      return null;
    }

    const baseConstraints = loosenConstraints
      ? {
          width: { ideal: 640, min: 320 },
          height: { ideal: 480, min: 240 },
          frameRate: { ideal: 24, min: 10 },
        }
      : {
          width: { ideal: 640 },
          height: { ideal: 480 },
          frameRate: { ideal: 30 },
        };

    if (forceDefaultDevice) {
      return baseConstraints;
    }

    if (selectedDeviceId) {
      return {
        ...baseConstraints,
        deviceId: { ideal: selectedDeviceId },
      };
    }

    return {
      ...baseConstraints,
      facingMode: { ideal: facingMode },
    };
  }, [facingMode, selectedDeviceId, forceDefaultDevice, loosenConstraints, useBareConstraints]);

  const toBase64 = (file) => {
    return new Promise((resolve, reject) => {
      if (!file) {
        reject(new Error("No file provided"));
        return;
      }

      // Validate file type
      const validTypes = ["image/jpeg", "image/png", "image/gif"];
      if (!validTypes.includes(file.type)) {
        reject(new Error("Only JPG, PNG, and GIF images are allowed"));
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        try {
          // Ensure the base64 string is properly formatted
          const result = reader.result;
          if (!result.startsWith("data:")) {
            throw new Error("Invalid file format");
          }
          // Return base64 without data URL prefix
          resolve(result.split(",")[1]);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });
  };

  const generateSafeFilename = (originalName) => {
    const timestamp = Date.now();
    const extension = originalName.substring(originalName.lastIndexOf(".")).toLowerCase();
    const baseName = originalName.substring(0, originalName.lastIndexOf("."));

    // Remove special characters and spaces
    const safeName = baseName.replace(/[^a-zA-Z0-9]/g, "_");

    return `${safeName}_${timestamp}${extension}`;
  };

  const handleUpdateMember = async (updatedMember) => {
    try {
      // The FamilyMemberDialog already handles the API call
      // We just need to update the local state and refresh
      console.log("Member updated:", updatedMember);
      console.log("Updated member work fields:", {
        companyname1: updatedMember.companyname1,
        designation1: updatedMember.designation1,
        employmenttype1: updatedMember.employmenttype1,
        startdate1: updatedMember.startdate1,
      });
      
      // Refresh family data from API
      if (refreshFamilyData) {
        await refreshFamilyData();
      }
      
      // If editing self, refresh user profile (check displayId for "Myself")
      if (editingMember?.displayId === "0" && refreshUserProfile) {
        await refreshUserProfile();
      }
      
      // Update parent component
      if (onUpdate) {
        onUpdate(updatedMember);
      }
      
      // Close dialog and show success
      setEditDialogOpen(false);
      setEditingMember(null);
      setSnackbarMessage("Family member updated successfully!");
      setSuccessSB(true);
    } catch (error) {
      console.error("Error in handleUpdateMember:", error);
      setSnackbarMessage("Failed to update family member");
      setErrorSB(true);
    }
  };

  const handleEditCancel = () => {
    setEditDialogOpen(false);
    setEditingMember(null);
    setError(null);
  };

  const handleCopyFamilyGroupId = async () => {
    if (!familyGroupId) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(familyGroupId);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = familyGroupId;
        textarea.setAttribute("readonly", "");
        textarea.style.position = "absolute";
        textarea.style.left = "-9999px";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }
      setSnackbarMessage("Family Group ID copied");
      setSuccessSB(true);
    } catch (err) {
      console.error("Failed to copy Family Group ID:", err);
      setSnackbarMessage("Failed to copy Family Group ID");
      setErrorSB(true);
    }
  };

  const isEditingSelf = (() => {
    if (!editingMember) return false;
    const linkedId = editingMember.linkedUserId ?? editingMember.linked_user_id_c ?? editingMember.linked_user_id ?? editingMember.linked_userid;
    if (linkedId != null) return String(linkedId) === String(currentUserId);
    return editingMember?.displayId === "0";
  })();
  const editMobileError = touchedFields.mobile && (!editFormData.mobile || !isValidPhoneNumber(editFormData.mobile));
  const femaleRelations = [
    "Wife",
    "Sister",
    "Mother",
    "Grandmother (Paternal)",
    "Grandmother (Maternal)",
    "Daughter",
    "Aunt",
    "Niece",
  ];
  const isFemaleRelation = editingMember
    ? femaleRelations.includes(editingMember.relationship)
    : false;
  const baseExistingPhoto = isEditingSelf
    ? userPhoto || editingMember?.photo || null
    : editingMember?.photo || null;
  const hadPhotoBeforeRemoval = Boolean(baseExistingPhoto);
  const avatarSrc = editFormData.photo
    ? editFormData.photo
    : editFormData.photoRemoved
    ? null
    : baseExistingPhoto;
  const defaultAvatarImage = isEditingSelf
    ? Male
    : isFemaleRelation
    ? Female
    : Male;
  const shouldShowRemovePhotoButton = Boolean(
    editFormData.photo || (!editFormData.photoRemoved && baseExistingPhoto)
  );

  return (
    <>
      <div className="bg-white rounded-lg shadow-xl p-3 sm:p-4 md:p-6 mt-4 md:mt-8">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4 mb-4">
          <div className="flex items-center gap-3">
            <h2 className="text-xl sm:text-2xl font-semibold text-gray-800">Family Members List</h2>
            {familyGroupId && (
              <span
                className="text-sm text-gray-500 cursor-pointer hover:underline"
                role="button"
                tabIndex={0}
                title="Click to copy"
                onClick={handleCopyFamilyGroupId}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleCopyFamilyGroupId();
                  }
                }}
              >
                Family Group ID: {familyGroupId}
              </span>
            )}
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-4 w-full sm:w-auto">
            <TextField
              variant="outlined"
              size="small"
              placeholder="Search by name, email, or phone number..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ 
                minWidth: { xs: '100%', sm: 250, md: 300 },
                '& .MuiInputBase-input': {
                  fontSize: { xs: '0.875rem', sm: '1rem' }
                }
              }}
              InputProps={{
                startAdornment: (
                  <Box sx={{ display: 'flex', alignItems: 'center', pr: 1 }}>
                    <Icon sx={{ fontSize: 18, color: 'text.secondary' }}>search</Icon>
                  </Box>
                ),
                endAdornment: searchTerm ? (
                  <IconButton
                    size="small"
                    onClick={() => setSearchTerm('')}
                    sx={{ p: 0.5 }}
                  >
                    <Icon sx={{ fontSize: 18, color: 'text.secondary' }}>clear</Icon>
                  </IconButton>
                ) : null,
              }}
            />
            {typeof onOpenAddFamily === 'function' && (
              <MDButton 
                variant="gradient" 
                color="primary" 
                onClick={onOpenAddFamily}
                sx={{
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  padding: { xs: '8px 16px', sm: '10px 20px' }
                }}
              >
                <span className="flex items-center"><span className="material-icons" style={{fontSize:'18px',marginRight:6}}>add</span><span className="hidden xs:inline sm:inline">Add Family Member</span><span className="inline xs:hidden sm:hidden">Add</span></span>
              </MDButton>
            )}
            <MDButton
              variant="outlined"
              color="primary"
              onClick={() => setLinkDialogOpen(true)}
              sx={{
                fontSize: { xs: '0.875rem', sm: '1rem' },
                padding: { xs: '8px 16px', sm: '10px 20px' },
                whiteSpace: 'nowrap'
              }}
            >
              <span className="flex items-center">
                <span className="material-icons" style={{ fontSize: '18px', marginRight: 6 }}>link</span>
                <span className="hidden xs:inline sm:inline">Link Existing Members</span>
                <span className="inline xs:hidden sm:hidden">Link</span>
              </span>
            </MDButton>
          </div>
        </div>
        <ul className="space-y-3 sm:space-y-4">
          {loading && (
            [...Array(4)].map((_, idx) => (
              <li key={`skeleton-${idx}`} className="flex flex-col md:flex-row justify-between items-start md:items-center p-3 sm:p-4 bg-gray-50 rounded-lg shadow">
                <div className="flex items-start w-full md:w-3/4 gap-3 sm:gap-4">
                  <Skeleton variant="circular" sx={{ width: { xs: 80, sm: 96, md: 128 }, height: { xs: 80, sm: 96, md: 128 } }} />
                  <div className="flex-1">
                    <Skeleton variant="text" sx={{ width: { xs: '90%', sm: 220 }, height: { xs: 24, sm: 28 } }} />
                    <Skeleton variant="text" sx={{ width: { xs: '100%', sm: 300 }, height: 20 }} />
                    <Skeleton variant="text" sx={{ width: { xs: '80%', sm: 200 }, height: 20 }} />
                  </div>
                </div>
                <div className="w-full md:w-auto mt-3 md:mt-0 flex justify-center gap-1.5 sm:gap-2">
                  <Skeleton variant="rectangular" sx={{ width: { xs: 60, sm: 72 }, height: { xs: 28, sm: 32 } }} />
                  <Skeleton variant="rectangular" sx={{ width: { xs: 60, sm: 72 }, height: { xs: 28, sm: 32 } }} />
                  <Skeleton variant="rectangular" sx={{ width: { xs: 60, sm: 72 }, height: { xs: 28, sm: 32 } }} />
                  <Skeleton variant="rectangular" sx={{ width: { xs: 60, sm: 72 }, height: { xs: 28, sm: 32 } }} />
                </div>
              </li>
            ))
          )}
          {filteredFamilyMembers.map((member) => {
            if (loading) return null;

            const normalizedGender = String(member.gender_c || member.gender || "")
              .trim()
              .toLowerCase();
            const fallbackAvatar = normalizedGender === "female" ? Female : Male;
            const profileImageSrc = member.photo || fallbackAvatar;

            return (
            <li
              key={member.id}
              className="flex flex-col md:flex-row justify-between items-start md:items-center p-3 sm:p-4 bg-gray-50 rounded-lg shadow transition hover:shadow-lg"
            >
              <div className="flex items-start w-full md:w-3/4 gap-3 sm:gap-4">
                <div className="w-20 h-20 sm:w-24 sm:h-24 md:w-28 md:h-28 rounded-full overflow-hidden flex-shrink-0 flex justify-center items-center">
                  <img
                    src={profileImageSrc}
                    alt={member.name}
                    className="w-20 h-20 sm:w-24 sm:h-24 md:w-28 md:h-28 rounded-full object-cover"
                  />
                </div>

                <div className="flex flex-col flex-1 min-w-0">
                  <p className="text-base sm:text-lg font-semibold text-gray-800 mb-1 break-words">
                    {member.name || 'Unnamed Member'}
                  </p>
                  {(() => {
                    const parentInfo = findParentText(member);
                    const spouseName = findSpouseName(member);
                    const childrenText = findChildrenText(member);
                    return (
                      <>
                        {parentInfo && (
                          <p className="text-xs sm:text-sm text-gray-600 break-words">
                            <span className="font-medium">{parentInfo.label}:</span> {parentInfo.text}
                          </p>
                        )}
                        {spouseName && (
                          <p className="text-xs sm:text-sm text-gray-600 break-words">
                            <span className="font-medium">Spouse:</span> {spouseName}
                          </p>
                        )}
                        {childrenText && (
                          <p className="text-xs sm:text-sm text-gray-600 break-words">
                            <span className="font-medium">Children:</span> {childrenText}
                          </p>
                        )}
                      </>
                    );
                  })()}
                  {/* Relationship field removed as requested */}
                  <p className="text-xs sm:text-sm text-gray-600 break-words">
                    <span className="font-medium">Mobile:</span> {member.mobile || "Not specified"}
                  </p>
                  <p className="text-xs sm:text-sm text-gray-600">
                    <span className="font-medium">Gender:</span> {member.gender_c || member.gender || "Not specified"}
                  </p>
                  {/* Alive Status */}
                  <p className="text-xs sm:text-sm">
                    <span className="font-medium">Status:</span>{" "}
                    <span
                      className={`font-semibold ${
                        !member.alivestatus_c || member.alivestatus_c.toLowerCase() === "alive"
                          ? "text-green-600"
                          : "text-red-600"
                      }`}
                    >
                      {!member.alivestatus_c || member.alivestatus_c.toLowerCase() === "alive" ? "Alive" : "Passed Away"}
                    </span>
                  </p>
                  {/* City moved into the address block below */}
                </div>
              </div>

              <div className="w-full md:w-auto mt-3 md:mt-0 flex flex-wrap sm:flex-nowrap justify-center md:justify-end gap-1.5 sm:gap-2">
                <>
                  <MDButton
                    variant="gradient"
                    color="success"
                    onClick={() => (isMyselfMember(member) ? navigate('/profile') : handleViewClick(member))}
                    size="small"
                    sx={{
                      fontSize: { xs: '1rem', sm: '0.875rem' },
                      padding: { xs: '8px', sm: '8px 16px' },
                      minWidth: { xs: '32px', sm: '80px' },
                      width: { xs: '32px', sm: 'auto' },
                      height: { xs: '32px', sm: 'auto' },
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      '& .MuiButton-startIcon': {
                        margin: { xs: 0, sm: '0 8px 0 -4px' }
                      }
                    }}
                  >
                    <VisibilityIcon sx={{ fontSize: { xs: '1.2rem', sm: '18px' }, display: { xs: 'block', sm: 'none' } }} />
                    <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 0.5 }}>
                      <VisibilityIcon sx={{ fontSize: '18px' }} />
                      <span>View</span>
                    </Box>
                  </MDButton>
                  <MDButton
                    variant="gradient"
                    color="secondary"
                    onClick={() => handleShareClick(member)}
                    disabled={isMyselfMember(member)}
                    size="small"
                    sx={{
                      fontSize: { xs: '1rem', sm: '0.875rem' },
                      padding: { xs: '8px', sm: '8px 16px' },
                      minWidth: { xs: '32px', sm: '80px' },
                      width: { xs: '32px', sm: 'auto' },
                      height: { xs: '32px', sm: 'auto' },
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      '& .MuiButton-startIcon': {
                        margin: { xs: 0, sm: '0 8px 0 -4px' }
                      }
                    }}
                  >
                    <ShareIcon sx={{ fontSize: { xs: '1.2rem', sm: '18px' }, display: { xs: 'block', sm: 'none' } }} />
                    <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 0.5 }}>
                      <ShareIcon sx={{ fontSize: '18px' }} />
                      <span>Share</span>
                    </Box>
                  </MDButton>
                  <MDButton
                    variant="gradient"
                    color="info"
                    onClick={() => handleEditClick(member)}
                    disabled={isMyselfMember(member)}
                    size="small"
                    sx={{
                     fontSize: { xs: '1rem', sm: '0.875rem' },
                      padding: { xs: '8px', sm: '8px 16px' },
                      minWidth: { xs: '32px', sm: '80px' },
                      width: { xs: '32px', sm: 'auto' },
                      height: { xs: '32px', sm: 'auto' },
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      '& .MuiButton-startIcon': {
                        margin: { xs: 0, sm: '0 8px 0 -4px' }
                      }
                    }}
                  >
                    <EditIcon sx={{ fontSize: { xs: '1.2rem', sm: '18px' }, display: { xs: 'block', sm: 'none' } }} />
                    <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 0.5 }}>
                      <EditIcon sx={{ fontSize: '18px' }} />
                      <span>Edit</span>
                    </Box>
                  </MDButton>
                  <MDButton
                    variant="gradient"
                    color="error"
                    onClick={() => onDelete(member.id)}
                    disabled={isMyselfMember(member)}
                    size="small"
                    sx={{
                     fontSize: { xs: '1rem', sm: '0.875rem' },
                      padding: { xs: '8px', sm: '8px 16px' },
                      minWidth: { xs: '32px', sm: '80px' },
                      width: { xs: '32px', sm: 'auto' },
                      height: { xs: '32px', sm: 'auto' },
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      '& .MuiButton-startIcon': {
                        margin: { xs: 0, sm: '0 8px 0 -4px' }
                      }
                    }}
                  >
                    <DeleteIcon sx={{ fontSize: { xs: '1.2rem', sm: '18px' }, display: { xs: 'block', sm: 'none' } }} />
                    <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 0.5 }}>
                      <DeleteIcon sx={{ fontSize: '18px' }} />
                      <span>Delete</span>
                    </Box>
                  </MDButton>
                </>
              </div>
            </li>
            );
          })}
        </ul>
      </div>

      {/* Edit Dialog - Using Unified FamilyMemberDialog */}
      <Dialog
        open={editDialogOpen}
        onClose={handleEditCancel}
        fullWidth
        maxWidth={false}
        fullScreen={window.innerWidth < 768}
        PaperProps={{
          sx: {
            width: { xs: '100vw', sm: '90vw', md: '80vw' },
            maxWidth: { xs: '100vw', sm: '90vw', md: '80vw' },
            maxHeight: { xs: '100vh', sm: '90vh' },
            margin: { xs: 0, sm: 2 },
          },
        }}
      >
        <DialogTitle className="text-xl sm:text-2xl font-semibold text-center text-gray-700 mb-4 sm:mb-6 px-3 sm:px-4">
          {editingMember?.displayId === "0" ? "Edit My Profile" : "Edit Family Member"}
          <IconButton
            onClick={handleEditCancel}
            sx={{ position: "absolute", right: { xs: 4, sm: 8 }, top: { xs: 4, sm: 8 } }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <div className="px-3 sm:px-4 md:px-6 py-2 sm:py-4 overflow-y-auto">
          <FamilyMemberDialog
            mode="edit"
            memberData={editingMember}
            onUpdateMember={handleUpdateMember}
            // Pass full list; dialog will handle excluding current/self entries
            familyMembers={familyMembers}
            onClose={handleEditCancel}
          />
        </div>
      </Dialog>

      {/* Camera Dialog */}
      <Dialog open={cameraDialogOpen} onClose={handleCloseCamera} maxWidth="sm" fullWidth>
        <DialogTitle>
          Take Photo
          <IconButton
            aria-label="close"
            onClick={handleCloseCamera}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ textAlign: "center", padding: "20px" }}>
          {!capturedImage ? (
            <Box sx={{ position: 'relative', display: 'inline-block' }}>
              {cameraError ? (
                <Box sx={{ 
                  width: 400, 
                  height: 300, 
                  display: 'flex', 
                  flexDirection: 'column',
                  alignItems: 'center', 
                  justifyContent: 'center',
                  border: '2px dashed #ccc',
                  borderRadius: '8px',
                  backgroundColor: '#f5f5f5'
                }}>
                  <Typography variant="h6" color="error" gutterBottom>
                    Camera Error
                  </Typography>
                  <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mb: 2, px: 2 }}>
                    {cameraError}
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={retryCamera}
                    startIcon={<CameraAltIcon />}
                    sx={{
                      fontWeight: 600,
                      color: '#fff',
                      backgroundColor: '#1976d2',
                      '&:hover': {
                        backgroundColor: '#115293',
                      },
                    }}
                  >
                    Retry Camera
                  </Button>
                </Box>
              ) : webcamKey < 0 ? (
                <Box sx={{ 
                  width: 400,
                  height: 300,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '2px dashed #ccc',
                  borderRadius: '8px',
                  backgroundColor: '#f5f5f5'
                }}>
                  <Typography variant="body1" color="text.secondary">
                    {cameraMessage || 'Preparing camera...'}
                  </Typography>
                </Box>
              ) : (
                <Webcam
                  key={`webcam-${webcamKey}-${facingMode}-${selectedDeviceId || 'default'}`} // Force re-render on retry
                  audio={false}
                  ref={webcamRef}
                  screenshotFormat="image/jpeg"
                  screenshotQuality={0.92}
                  width={400}
                  height={300}
                  style={{ borderRadius: "8px", objectFit: "cover" }}
                  videoConstraints={videoConstraints ?? undefined}
                  mirrored={facingMode === 'user'}
                  onUserMediaError={handleCameraError}
                  onUserMedia={handleCameraReady}
                />
              )}
              {!cameraReady && !cameraError && webcamKey >= 0 && (
                <Box sx={{ 
                  position: 'absolute', 
                  top: '50%', 
                  left: '50%', 
                  transform: 'translate(-50%, -50%)',
                  color: 'white',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
                  zIndex: 1
                }}>
                  {cameraMessage || 'Loading camera...'}
                </Box>
              )}
              <Box sx={{ mt: 2, display: "flex", justifyContent: "center", gap: 2, flexWrap: "wrap" }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={capturePhoto}
                  disabled={!cameraReady || cameraError}
                  sx={{
                    fontWeight: 600,
                    color: '#fff',
                    backgroundColor: '#1976d2',
                    '&:hover': {
                      backgroundColor: '#115293',
                    },
                    '&.Mui-disabled': {
                      backgroundColor: 'rgba(25, 118, 210, 0.4)',
                      color: '#fff',
                    },
                  }}
                >
                  Capture Photo
                </Button>
                <Button
                  variant="outlined"
                  onClick={switchCamera}
                  startIcon={<CameraAltIcon />}
                  disabled={!cameraReady || cameraError || videoDevices.length <= 1}
                  sx={{
                    fontWeight: 600,
                    color: '#1976d2',
                    borderColor: '#1976d2',
                    '&:hover': {
                      borderColor: '#115293',
                      color: '#115293',
                    },
                    '&.Mui-disabled': {
                      borderColor: 'rgba(25, 118, 210, 0.4)',
                      color: 'rgba(25, 118, 210, 0.4)',
                    },
                  }}
                >
                  Switch Camera
                </Button>
              </Box>
              {cameraMessage && cameraReady && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {cameraMessage}
                </Typography>
              )}
            </Box>
          ) : (
            <Box>
              <img
                src={capturedImage}
                alt="Captured"
                style={{
                  maxWidth: "100%",
                  maxHeight: "300px",
                  borderRadius: "8px",
                }}
              />
              <Box sx={{ mt: 2, display: "flex", justifyContent: "center", gap: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleConfirmPhoto}
                  sx={{
                    fontWeight: 600,
                    color: '#fff',
                    backgroundColor: '#1976d2',
                    '&:hover': {
                      backgroundColor: '#115293',
                    },
                  }}
                >
                  Confirm
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleRetakePhoto}
                  sx={{
                    fontWeight: 600,
                    color: '#1976d2',
                    borderColor: '#1976d2',
                    '&:hover': {
                      borderColor: '#115293',
                      color: '#115293',
                    },
                  }}
                >
                  Retake
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={handleCloseCamera}
                  sx={{
                    fontWeight: 600,
                    color: '#d32f2f',
                    borderColor: '#d32f2f',
                    '&:hover': {
                      borderColor: '#b71c1c',
                      color: '#b71c1c',
                    },
                  }}
                >
                  Cancel
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <ExistingMemberLinkDialog
        open={linkDialogOpen}
        onClose={() => setLinkDialogOpen(false)}
        members={displayFamilyMembers}
        onLinkSuccess={async () => {
          if (typeof refreshFamilyData === "function") {
            try {
              await refreshFamilyData();
            } catch (refreshError) {
              console.error("Failed to refresh family data after link:", refreshError);
            }
          }
        }}
      />

      {/* View Family Member Details Dialog */}
      <ViewFamilyMemberDialog
        open={viewDialogOpen}
        onClose={handleViewClose}
        member={viewingMember}
        familyMembers={displayFamilyMembers}
        sessionId={sessionId}
        userId={useSelector((state) => state.session.setUserId ? state.session.setUserId.value : null)}
        user={user}
        userPhoto={userPhoto}
        onEdit={handleEditClick}
        refreshCallback={() => {
          // Refresh family data after operations
          if (refreshFamilyData) {
            refreshFamilyData();
          }
        }}
      />

      {/* Share dialog (opened from list) */}
      <ShareProfileDialog
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        profile={shareMember}
        educationEntries={shareEducationEntries}
        workEntries={shareWorkEntries}
        idCardEntries={shareIDCardEntries}
        bankEntries={shareBankEntries}
        addressEntries={shareAddressEntries}
        onShare={(result) => {
          if (result && result.success) {
            setSnackbarMessage(result.preview ? String(result.preview).slice(0, 300) : 'Shared successfully');
            setSuccessSB(true);
          } else {
            setSnackbarMessage(result && result.message ? result.message : 'Failed to share');
            setErrorSB(true);
          }
        }}
      />

      {/* Success Snackbar */}
      <MDSnackbar
        color="success"
        icon="check"
        title="Success"
        content={snackbarMessage}
        dateTime="just now"
        open={successSB}
        onClose={() => setSuccessSB(false)}
        close={() => setSuccessSB(false)}
        bgWhite
      />

      {/* Error Snackbar */}
      <MDSnackbar
        color="error"
        icon="warning"
        title="Error"
        content={snackbarMessage}
        dateTime="just now"
        open={errorSB}
        onClose={() => setErrorSB(false)}
        close={() => setErrorSB(false)}
        bgWhite
      />
    </>
  );
};

export default FamilyMembersList;
